import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getStoredToken } from '@/features/auth/utils/authStorage';

const API_BASE_URL = '/api';

/**
 * Event types that can be received from the SSE connection.
 * These match the Rust `SimulationEvent` enum in the backend.
 */
export type SimulationEventType =
  | 'connected'
  | 'feature_created'
  | 'feature_updated'
  | 'feature_deleted'
  | 'features_reordered'
  | 'team_created'
  | 'team_updated'
  | 'team_deleted'
  | 'teams_reordered'
  | 'deliverable_created'
  | 'deliverable_updated'
  | 'deliverable_deleted'
  | 'deliverables_reordered'
  | 'dependency_created'
  | 'dependency_deleted'
  | 'dependencies_cleared'
  | 'simulation_run_started'
  | 'simulation_run_completed'
  | 'simulation_run_failed';

/**
 * Feature response data - matches backend FeatureResponse
 */
export interface FeatureEventData {
  id: string;
  name: string;
  source_id: string | null;
  due_date: string | null;
  created_at: string;
}

/**
 * Team response data
 */
export interface TeamEventData {
  id: string;
  name: string;
  wip: number;
  throughput_data: number[];
  throughput_scale: number;
  position: number;
  color: string | null;
  deliverables: DeliverableEventData[];
}

/**
 * Deliverable response data
 */
export interface DeliverableEventData {
  id: string;
  team_id: string | null;
  feature_id: string | null;
  feature_name: string | null;
  name: string;
  unfinished_work_items: number;
  unknown_min: number | null; // null = not decided, 0 = deliberately no uncertainty
  unknown_max: number | null; // null = not decided, 0 = deliberately no uncertainty
  due_date: string | null;
  available_to_start_min: string | null;
  available_to_start_max: string | null;
  position: number;
  predecessors: string[];
  team_name: string | null;
}

/**
 * Base event structure from SSE
 */
export interface SimulationEvent {
  type: SimulationEventType;
  data: unknown;
}

/**
 * Event handlers for simulation real-time events.
 */
export interface SimulationEventHandlers {
  /**
   * Called when any data-changing event is received (all events except 'connected' and run lifecycle).
   * Use this to trigger cache invalidation - the event type is provided for logging.
   */
  onChanged?: (eventType: SimulationEventType) => void;
  /** Called when SSE connection is established */
  onConnected?: () => void;
  /** Called when SSE connection is lost */
  onDisconnected?: () => void;
  /** Called on connection error */
  onError?: (error: Error) => void;
  /** Called when polling mode triggers a refresh (SSE fallback) */
  onRefresh?: () => void;
  /** Called when a simulation run has started in the background */
  onRunStarted?: () => void;
  /** Called when a background simulation run completes successfully */
  onRunCompleted?: (data: { ran_at: string; num_simulations: number }) => void;
  /** Called when a background simulation run fails */
  onRunFailed?: (data: { error: string }) => void;
}

/**
 * Return type for the useSimulationEvents hook
 */
export interface UseSimulationEventsResult {
  /** Whether the SSE connection is active */
  connected: boolean;
  /** Whether polling mode is active (SSE fallback) */
  polling: boolean;
  /** Error if connection failed */
  error: Error | null;
  /** Manually disconnect the SSE connection */
  disconnect: () => void;
  /** Manually reconnect the SSE connection (tries SSE first) */
  reconnect: () => void;
  /** Manually switch to polling mode (skips SSE entirely) */
  switchToPolling: () => void;
}

/**
 * Hook for subscribing to real-time simulation events via SSE.
 *
 * This hook manages the SSE connection lifecycle, including:
 * - Automatic connection when simulationId is provided
 * - Automatic reconnection with exponential backoff
 * - Proper cleanup on unmount
 *
 * @param simulationId - The UUID of the simulation to subscribe to
 * @param handlers - Event handlers for each event type
 * @returns Connection state and control functions
 *
 * @example
 * ```tsx
 * const { connected, error } = useSimulationEvents(simulationId, {
 *   onDeliverableUpdated: (deliverable) => {
 *     // Update form state with new deliverable data
 *   },
 *   onFeatureCreated: (feature) => {
 *     // Add new feature to form state
 *   },
 * });
 * ```
 */
/** Polling interval in milliseconds (5 seconds) - reduced frequency to minimize re-renders */
const POLLING_INTERVAL = 5000;
/** SSE connection timeout - if no connection within this time, switch to polling */
const SSE_CONNECTION_TIMEOUT = 5000;

export function useSimulationEvents(
  simulationId: string | null | undefined,
  handlers: SimulationEventHandlers,
): UseSimulationEventsResult {
  const [connected, setConnected] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use refs to avoid recreating the EventSource on handler changes.
  // The ref is updated in an effect (not during render) so callbacks always
  // read the latest handlers without re-subscribing the SSE connection.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Track reconnection state
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Holds the latest `connect` so the reconnect timer can re-invoke it without
  // a stale closure (and without `connect` referencing itself before declaration).
  const connectRef = useRef<() => void>(() => {});
  // Track if polling mode is explicitly preferred (user choice or SSE failed completely)
  const pollingPreferredRef = useRef(false);
  const maxReconnectAttempts = 5;

  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    console.log('[SSE] Starting polling mode');
    // Mark polling as preferred - don't try SSE anymore
    pollingPreferredRef.current = true;
    setPolling(true);
    setError(null);

    // Close any existing SSE connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnected(false);

    // Initial refresh
    handlersRef.current.onRefresh?.();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(() => {
      console.log('[Polling] Triggering refresh');
      handlersRef.current.onRefresh?.();
    }, POLLING_INTERVAL);
  }, []);

  const disconnect = useCallback(() => {
    clearConnectionTimeout();
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    stopPolling();
    setConnected(false);
  }, [stopPolling, clearConnectionTimeout]);

  const connect = useCallback(() => {
    if (!simulationId) {
      console.log('[SSE] No simulationId, skipping connection');
      return;
    }

    // If polling is preferred, don't try SSE - start polling instead
    if (pollingPreferredRef.current) {
      console.log('[SSE] Polling mode preferred, skipping SSE connection');
      if (!pollingIntervalRef.current) {
        startPolling();
      }
      return;
    }

    // Optimistic polling: start polling immediately while trying SSE
    // This eliminates the 5-second wait if SSE doesn't work
    if (!pollingIntervalRef.current) {
      console.log('[SSE] Starting optimistic polling while attempting SSE connection');
      setPolling(true);
      handlersRef.current.onRefresh?.();
      pollingIntervalRef.current = setInterval(() => {
        console.log('[Polling] Triggering refresh (optimistic)');
        handlersRef.current.onRefresh?.();
      }, POLLING_INTERVAL);
    }

    // Close any existing connection
    if (abortControllerRef.current) {
      console.log('[SSE] Closing existing connection');
      abortControllerRef.current.abort();
    }

    const url = `${API_BASE_URL}/simulations/${simulationId}/events`;
    console.log('[SSE] Connecting to:', url);

    // Create abort controller for this connection
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Get auth token
    const token = getStoredToken();

    // Set a connection timeout - if SSE doesn't connect quickly, keep polling
    clearConnectionTimeout();
    connectionTimeoutRef.current = setTimeout(() => {
      console.log('[SSE] Connection timeout - SSE not working, continuing with polling');
      abortController.abort();
      abortControllerRef.current = null;
      // Mark polling as preferred so we don't retry SSE
      pollingPreferredRef.current = true;
    }, SSE_CONNECTION_TIMEOUT);

    // Use fetchEventSource which supports custom headers
    fetchEventSource(url, {
      signal: abortController.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      onopen: async (response) => {
        if (response.ok) {
          console.log('[SSE] Connection opened for simulation:', simulationId);
          // Clear the connection timeout - SSE is working!
          clearConnectionTimeout();
          // SSE connected - stop optimistic polling
          if (pollingIntervalRef.current) {
            console.log('[SSE] Stopping optimistic polling - SSE connected');
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setPolling(false);
          }
          setConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;
          handlersRef.current.onConnected?.();
        } else {
          console.error('[SSE] Connection failed:', response.status, response.statusText);
          throw new Error(`SSE connection failed: ${response.status}`);
        }
      },
      onmessage: (event) => {
        // Skip empty events (keepalive pings, etc.)
        if (!event.data || event.data.trim() === '') {
          console.log('[SSE] Received empty event, skipping');
          return;
        }

        // Skip keepalive ping comments that might leak through
        if (event.data === 'ping' || event.data.startsWith(':')) {
          console.log('[SSE] Received keepalive, skipping');
          return;
        }

        // Validate that data looks like JSON before parsing
        if (!event.data.startsWith('{')) {
          console.warn('[SSE] Received non-JSON data, skipping:', event.data.substring(0, 100));
          return;
        }

        console.log('[SSE] Received message:', event.data.substring(0, 200));
        try {
          const parsed = JSON.parse(event.data) as SimulationEvent;
          const { type, data } = parsed;
          console.log('[SSE] Parsed event:', type, data);

          // Handle events
          if (type === 'connected') {
            // Server confirmed SSE connection
            console.log(
              '[SSE] Server confirmed connection for simulation:',
              (data as { simulation_id: string }).simulation_id,
            );
          } else if (type === 'simulation_run_started') {
            handlersRef.current.onRunStarted?.();
          } else if (type === 'simulation_run_completed') {
            handlersRef.current.onRunCompleted?.(data as { ran_at: string; num_simulations: number });
          } else if (type === 'simulation_run_failed') {
            handlersRef.current.onRunFailed?.(data as { error: string });
          } else {
            // All other events are data changes - notify via onChanged
            handlersRef.current.onChanged?.(type);
          }
        } catch (e) {
          // Log truncated data for debugging (first 500 chars)
          console.error('[SSE] Failed to parse event:', e, '\nData (truncated):', event.data.substring(0, 500));
        }
      },
      onerror: (err) => {
        console.error('[SSE] Connection error:', err);
        // Clear connection timeout since we got a response (even if error)
        clearConnectionTimeout();
        abortControllerRef.current = null;
        setConnected(false);
        handlersRef.current.onDisconnected?.();

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current();
          }, delay);
        } else {
          // SSE failed completely - fall back to polling
          console.log('[SSE] Max reconnect attempts reached, falling back to polling');
          startPolling();
        }

        // Throw to stop the retry loop in fetchEventSource
        throw err;
      },
      onclose: () => {
        console.log('[SSE] Connection closed');
        // Don't reconnect if we aborted intentionally
        if (!abortController.signal.aborted) {
          setConnected(false);
          handlersRef.current.onDisconnected?.();
        }
      },
    }).catch((err) => {
      // Ignore abort errors (intentional disconnection)
      if (err.name !== 'AbortError') {
        console.error('[SSE] Fetch error:', err);
      }
    });
  }, [simulationId, startPolling, clearConnectionTimeout]);

  // Keep the reconnect timer pointed at the latest `connect`.
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    console.log('[SSE] Manually reconnecting - will try SSE first');
    reconnectAttemptsRef.current = 0;
    setError(null);
    // Reset polling preference so we try SSE again
    pollingPreferredRef.current = false;
    stopPolling();
    connect();
  }, [connect, stopPolling]);

  const switchToPolling = useCallback(() => {
    console.log('[SSE] Manually switching to polling mode');
    // startPolling handles closing SSE and setting pollingPreferred
    startPolling();
  }, [startPolling]);

  // Connect when simulationId changes
  useEffect(() => {
    if (simulationId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [simulationId, connect, disconnect]);

  return {
    connected,
    polling,
    error,
    disconnect,
    reconnect,
    switchToPolling,
  };
}
